import React, {FC, useEffect, useState} from "react";
import {
  Box,
  CardMedia,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@material-ui/core";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FlexBox from "view/components/Layout/FlexBox";
import {grey} from "@material-ui/core/colors";
import {orderBy} from "lodash";
import {getFileType} from "view/apps/Organiser/pages/Show/Settings/ElectronicSignature/EditModal/FilePreview";
import {ISignature} from "@evennt-tech/api-v2-core";
import {useStyles} from "./styles";
import {ViewModeEnum} from "../types";

type Props = {
  signatures: ISignature[];
  selectedOption: ViewModeEnum;
  setSelectedOption: React.Dispatch<React.SetStateAction<ViewModeEnum>>;
  selectedSignatureId: string | null;
  setSelectedSignatureId: React.Dispatch<React.SetStateAction<string | null>>;
  signatureOptionContainerClass: string;
  radioGroupClass: string;
  labelTextClass: string;
};

const ExistingSignaturesContainer: FC<Props> = ({
  signatures,
  selectedOption,
  setSelectedOption,
  selectedSignatureId,
  setSelectedSignatureId,
  signatureOptionContainerClass,
  radioGroupClass,
  labelTextClass,
}) => {
  const classes = useStyles();
  const orderedSignatures = orderBy(signatures, ["name"], ["asc"]);
  const [fileTypes, setFileTypes] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (signatures.length > 0) {
      fetchFileTypes();
    }
  }, [signatures]);

  const fetchFileTypes = async () => {
    const types: {[key: string]: string} = {};
    const typePromises = signatures.map(async (signature) => {
      if (signature.file?.url) {
        const type = await getFileType(signature.file.url);
        types[signature.id] = type;
      }
    });

    await Promise.all(typePromises);
    setFileTypes(types);
  };
  const handleSignatureSelect = (signature: ISignature) => {
    if (selectedSignatureId && selectedSignatureId === signature.id) {
      setSelectedSignatureId(null);
    } else {
      setSelectedSignatureId(signature.id);
    }
  };

  return (
    <Box className={signatureOptionContainerClass}>
      <RadioGroup
        value={selectedOption}
        onChange={() => {
          setSelectedOption(ViewModeEnum.saved);
        }}
        className={radioGroupClass}
      >
        <FormControlLabel
          value={ViewModeEnum.saved}
          control={<Radio color="primary" />}
          label={
            <FlexBox alignCenter>
              <Typography
                variant="body1"
                className={labelTextClass}
                style={{
                  color: selectedOption === "saved" ? "black" : grey[600],
                }}
              >
                Use Saved Signature
              </Typography>
              {signatures.length === 0 && (
                <Tooltip
                  title="Create your Signature in the Show Settings"
                  arrow
                >
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </Tooltip>
              )}
            </FlexBox>
          }
          disabled={signatures.length === 0}
        />

        {selectedOption === "saved" && signatures.length > 0 && (
          <Box className={classes.savedSignatureContainer}>
            {orderedSignatures.map((signature) => (
              <FlexBox
                key={signature.id}
                alignCenter
                className={`${classes.signatureItem} ${
                  selectedSignatureId === signature.id
                    ? classes.selectedSignatureItem
                    : ""
                }`}
                onClick={() => handleSignatureSelect(signature)}
              >
                <Box>
                  <Typography className={classes.signatureText}>
                    {signature.name},
                  </Typography>
                  <Typography className={classes.signatureText}>
                    {signature.role}
                  </Typography>
                </Box>
                {fileTypes[signature.id] !== "application/pdf" ? (
                  <CardMedia
                    component="img"
                    image={signature.file.url}
                    alt={signature.name}
                    className={
                      signature.type === "text"
                        ? classes.avatarText
                        : classes.avatar
                    }
                  />
                ) : (
                  <PictureAsPdfIcon
                    color="disabled"
                    className={classes.avatar}
                  />
                )}
              </FlexBox>
            ))}
          </Box>
        )}
      </RadioGroup>
    </Box>
  );
};

export default ExistingSignaturesContainer;
